import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore, hasRole, homePathFor } from '../../store/useAuthStore';

/**
 * Route guard.
 *
 * This is a *convenience*, not a security boundary — the API enforces every
 * rule independently. Its job is to avoid rendering a console a user cannot
 * use, and to send them somewhere sensible instead.
 *
 * While status is 'unknown' it renders a spinner rather than redirecting, or a
 * hard refresh would bounce a signed-in user to /login before /auth/me returns.
 */
export default function ProtectedRoute({ minimumRole = 'customer', children }) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const expired = useAuthStore((s) => s.expired);

  if (status === 'unknown' || status === 'authenticating') {
    return (
      <div className="grid h-full place-items-center" role="status" aria-label="Checking your session">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }

  if (status !== 'signed_in') {
    // Remember where they were headed so login can return them there, and pass
    // on whether this was an expiry rather than a fresh visit — otherwise being
    // bounced to a login screen mid-task looks like the app lost their work for
    // no reason.
    return <Navigate to="/login" replace state={{ from: location.pathname, expired }} />;
  }

  // A provisioned account can reach exactly one screen until it has a password
  // of its own. The API enforces this with a 403 on everything else; without
  // the matching redirect the user would land on a console full of failed
  // requests and no explanation.
  if (user.must_change_password && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  if (!hasRole(user.role, minimumRole)) {
    // Signed in, wrong role: send them to their own home rather than /login,
    // which would look like the session had expired.
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  return children;
}
